import type { View } from "../../../../common/types.ts";
import { types } from "./transactionInfo.ts";

type NewsEvent = View<"news">["events"][number];

export type NewsImportanceTier = "high" | "medium" | "ordinary";
export type NewsImpactKind = "draft" | "league" | "market" | "roster" | "standings" | "star";
export type NewsRelevanceKind = "userTeam" | "otherTeam" | "league";

export const getNewsRelevanceKind = (
  event: Partial<Pick<NewsEvent, "tid" | "tids">>,
  userTid: number,
): NewsRelevanceKind => {
  if (event.tid === userTid || event.tids?.includes(userTid)) {
    return "userTeam";
  }
  if (event.tid !== undefined || (event.tids && event.tids.length > 0)) {
    return "otherTeam";
  }
  return "league";
};

export const getNewsImportanceTier = (
  event: Partial<Pick<NewsEvent, "score">>,
): NewsImportanceTier => {
  const score = event.score ?? 0;
  if (score >= 20) {
    return "high";
  }
  if (score >= 10) {
    return "medium";
  }
  return "ordinary";
};

export const getNewsCategory = (event: Pick<NewsEvent, "type">) =>
  types[event.type]?.category ?? "league";

export const getNewsImpactKind = (event: Pick<NewsEvent, "type">): NewsImpactKind => {
  const category = getNewsCategory(event);

  if (category === "injury") {
    return "roster";
  }
  if (category === "transaction") {
    return "market";
  }
  if (category === "playoffs" || category === "team") {
    return "standings";
  }
  if (category === "draft") {
    return "draft";
  }
  if (category === "award" || category === "playerFeat") {
    return "star";
  }
  return "league";
};

export const getNewsHeadlineEvent = (events: NewsEvent[], userTid: number) =>
  events.find((event) => getNewsRelevanceKind(event, userTid) === "userTeam") ?? events[0];

export type NewsPriorityGroup = "userTeam" | "leagueMajor" | "other";

// One news item belongs to exactly one priority group, highest first:
// 本队相关 (userTeam) > 联赛大事 (leagueMajor, non-user high/medium impact) >
// 其他 (other, ordinary league noise). This is what keeps a single event from
// being rendered in multiple feed segments at once (法则二).
export const getNewsPriorityGroup = (
  event: Partial<Pick<NewsEvent, "tid" | "tids" | "score">>,
  userTid: number,
): NewsPriorityGroup => {
  if (getNewsRelevanceKind(event, userTid) === "userTeam") {
    return "userTeam";
  }
  if (getNewsImportanceTier(event) !== "ordinary") {
    return "leagueMajor";
  }
  return "other";
};

export const groupNewsByPriority = (events: NewsEvent[], userTid: number) => {
  const userTeam: NewsEvent[] = [];
  const leagueMajor: NewsEvent[] = [];
  const other: NewsEvent[] = [];

  for (const event of events) {
    const group = getNewsPriorityGroup(event, userTid);
    if (group === "userTeam") {
      userTeam.push(event);
    } else if (group === "leagueMajor") {
      leagueMajor.push(event);
    } else {
      other.push(event);
    }
  }

  return { leagueMajor, other, userTeam };
};

export const groupNewsEventsBySeason = (events: NewsEvent[]) => {
  const groups = new Map<number, NewsEvent[]>();
  for (const event of events) {
    const season = event.season ?? 0;
    groups.set(season, [...(groups.get(season) ?? []), event]);
  }

  return [...groups.entries()].map(([season, groupedEvents]) => ({
    events: groupedEvents,
    season,
  }));
};
